import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, message, Space, Popconfirm, Modal } from 'antd'; // <-- Imported Modal
import { useNavigate } from 'react-router-dom';
import App from "../../Utils/LayoutMobile"; 
import dayjs from 'dayjs';

// ── Theme Constants ──
const C_PINK      = "#F04DA0";
const C_PURPLE    = "#8B2FC9";
const C_GRAD      = `linear-gradient(135deg, ${C_PINK}, ${C_PURPLE})`;
const C_SOFT_BG   = "#F8F4FF";
const C_TEXT_MAIN = "#1a0040";
const C_TEXT_MUTED= "#64748B";

// ── Icons ──
const MailIcon = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
const PhoneIcon = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const UserIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const CalendarIcon = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const DocIcon = () => <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>;
const ExtIcon = () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>;
const TrashIcon = () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>;
const EyeIcon = () => <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>; // <-- NEW

// ── Section Label ──
function SectionLabel({ children }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.16em", textTransform:"uppercase", color:C_PINK, whiteSpace:"nowrap" }}>
        {children}
      </span>
      <span style={{ flex:1, height:1.5, background:`linear-gradient(90deg, rgba(240,77,160,0.35), transparent)`, borderRadius:2 }}/>
    </div>
  );
}

const AbstractList = () => {
  const [abstracts, setAbstracts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // <-- NEW: State for Modal -->
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAbstract, setSelectedAbstract] = useState(null);

  const navigate = useNavigate(); 

  const fetchAbstracts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/v1/Abstract/abstract`);
      setAbstracts(response.data);
    } catch (err) {
      message.error('Failed to load abstracts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAbstracts();
  }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/v1/Abstract/abstract/${id}`);
      message.success('Abstract deleted successfully');
      setAbstracts(abstracts.filter(item => item._id !== id));
    } catch (err) {
      console.error(err);
      message.error('Failed to delete abstract');
    }
  };

  // <-- NEW: Handlers for Modal -->
  const handleViewDetails = (record) => {
    setSelectedAbstract(record);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedAbstract(null);
  };

  const columns = [
    {
      title: 'Submitter Details',
      key: 'submitter',
      width: 250,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "flex-start", gap: "14px" }}>
          <div className="avatar-wrap">
            <UserIcon />
          </div>
          <div>
            <div style={{ fontWeight: 800, color: C_TEXT_MAIN, fontSize: "14.5px", marginBottom: "4px" }}>
              {record.name || "Anonymous"}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
              <span style={{ display: "flex", alignItems: "center", gap: "6px", color: C_TEXT_MUTED, fontSize: "12.5px" }}>
                <span style={{ color: C_PURPLE }}><MailIcon /></span> {record.email || "No email"}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: "6px", color: C_TEXT_MUTED, fontSize: "12.5px" }}>
                <span style={{ color: C_PINK }}><PhoneIcon /></span> {record.mobile || "No phone"}
              </span>
            </div>
          </div>
        </div>
      ),
    },
    // <-- NEW: Summary Column -->
    {
      title: 'Summary',
      dataIndex: 'abstract',
      key: 'abstract',
      render: (text) => (
        <div style={{ maxWidth: "220px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: C_TEXT_MUTED, fontSize: "13.5px" }} title={text}>
          {text ? text : <span style={{ fontStyle: "italic", opacity: 0.6 }}>No summary</span>}
        </div>
      ),
    },
    {
      title: 'Submission Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: C_TEXT_MAIN, fontSize: "13px", fontWeight: 600 }}>
          <span style={{ color: C_PURPLE }}><CalendarIcon /></span>
          {date ? dayjs(date).format('MMM D, YYYY - h:mm A') : "Unknown"}
        </div>
      ),
    },
    {
      title: 'Document',
      key: 'pdf',
      align: 'center',
      render: (_, record) =>
        record.pdfUrl ? (
          <a href={record.pdfUrl} target="_blank" rel="noopener noreferrer" className="pdf-btn">
            <DocIcon /> PDF
          </a>
        ) : (
          <span className="no-pdf-badge">No File</span>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
          {/* <-- NEW: View Button --> */}
          <button className="action-btn view-btn" title="View Details" onClick={() => handleViewDetails(record)}>
            <EyeIcon />
          </button>

          <Popconfirm
            title={<span style={{ fontWeight: 700, color: C_TEXT_MAIN }}>Delete Abstract?</span>}
            description="Are you sure you want to permanently delete this abstract?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes, delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true, style: { borderRadius: "8px", fontWeight: 600 } }}
            cancelButtonProps={{ style: { borderRadius: "8px", fontWeight: 600 } }}
          >
            <button className="action-btn delete-btn" title="Delete Abstract">
              <TrashIcon />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const sortedData = [...abstracts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  return (
    <App>
      <div className="lc">
        <div className="mw">
          
          <div className="header-flex">
            <div>
              <SectionLabel>Submissions</SectionLabel>
              <h1 className="sec-title">Abstract <span style={{ color: C_PINK }}>List</span></h1>
              <p className="sec-sub">Review and download submitted abstracts from delegates and speakers.</p>
            </div>
          </div>

          <div className="glass table-wrapper">
            <Table
              columns={columns}
              dataSource={sortedData}
              rowKey="_id"
              loading={loading}
              pagination={{ 
                pageSize: 10,
                position: ["bottomCenter"],
                showSizeChanger: true
              }}
              className="premium-table"
              locale={{ 
                emptyText: (
                  <div className="empty-state">
                    <DocIcon />
                    <p>No abstracts have been submitted yet.</p>
                  </div>
                ) 
              }}
            />
          </div>

        </div>
      </div>

      {/* ── NEW: Abstract Details Modal ── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="avatar-wrap" style={{ width: '32px', height: '32px', borderRadius: '8px' }}>
              <DocIcon />
            </div>
            <span style={{ fontWeight: 800, fontSize: '18px', color: C_TEXT_MAIN }}>Abstract Details</span>
          </div>
        }
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width={700}
        centered
        className="abstract-modal"
      >
        {selectedAbstract && (
          <div className="modal-inner">
            <div className="modal-grid">
              <div className="modal-field">
                <span className="modal-lbl">Author Name</span>
                <span className="modal-val">{selectedAbstract.name}</span>
              </div>
              <div className="modal-field">
                <span className="modal-lbl">Email Address</span>
                <span className="modal-val">{selectedAbstract.email}</span>
              </div>
              <div className="modal-field">
                <span className="modal-lbl">Mobile Number</span>
                <span className="modal-val">{selectedAbstract.mobile}</span>
              </div>
              <div className="modal-field">
                <span className="modal-lbl">Institution / Hospital</span>
                <span className="modal-val">{selectedAbstract.institution}</span>
              </div>
            </div>

            <div className="modal-divider" />

            <div className="modal-grid" style={{ marginBottom: "16px" }}>
              <div className="modal-field" style={{ gridColumn: "1 / -1" }}>
                <span className="modal-lbl">Abstract Title</span>
                <span className="modal-val" style={{ fontSize: "16px", fontWeight: 700 }}>{selectedAbstract.title}</span>
              </div>
              <div className="modal-field">
                <span className="modal-lbl">Category</span>
                <span className="modal-badge">{selectedAbstract.category}</span>
              </div>
              <div className="modal-field">
                <span className="modal-lbl">Submitted On</span>
                <span className="modal-val">{dayjs(selectedAbstract.createdAt).format('MMMM D, YYYY - h:mm A')}</span>
              </div>
            </div>

            <div className="modal-field" style={{ marginBottom: "20px" }}>
              <span className="modal-lbl">Abstract Summary</span>
              <div className="modal-abstract-box">
                {selectedAbstract.abstract || "No summary provided by the author."}
              </div>
            </div>

            {selectedAbstract.pdfUrl && (
              <a href={selectedAbstract.pdfUrl} target="_blank" rel="noopener noreferrer" className="modal-pdf-btn">
                <DocIcon /> View Full PDF Document <ExtIcon />
              </a>
            )}
          </div>
        )}
      </Modal>

      {/* ── Styles ── */}
      <style>{`
        * { box-sizing: border-box; }

        .lc { 
          font-family: 'Segoe UI', sans-serif; 
          background: ${C_SOFT_BG}; 
          min-height: 100vh;
          padding: 40px 0 80px; 
        }
        .mw { 
          max-width: 1200px; 
          margin: 0 auto; 
          padding: 0 24px;
        }

        .header-flex {
          display: flex; align-items: flex-end; justify-content: space-between;
          margin-bottom: 40px; gap: 20px; flex-wrap: wrap;
        }
        .sec-title { 
          font-size: clamp(28px, 4vw, 38px); font-weight: 800; color: ${C_TEXT_MAIN}; 
          letter-spacing: -0.02em; line-height: 1.15; margin-bottom: 8px; margin-top: 0;
        }
        .sec-sub { font-size: 15px; color: ${C_TEXT_MUTED}; margin: 0; }

        .action-btn { 
          width: 36px; height: 36px; border-radius: 10px; border: none; 
          display: flex; align-items: center; justify-content: center; 
          cursor: pointer; transition: all 0.2s; 
        }
        /* <-- NEW: View Button Styles --> */
        .view-btn { 
          background: rgba(139,47,201,0.08); color: ${C_PURPLE}; 
        }
        .view-btn:hover { 
          background: rgba(139,47,201,0.15); transform: translateY(-2px); 
        }
        .delete-btn { 
          background: rgba(248, 113, 113, 0.1); color: #ef4444; 
        }
        .delete-btn:hover { 
          background: rgba(248, 113, 113, 0.2); transform: translateY(-2px); 
        }

        .avatar-wrap {
          width: 42px; height: 42px; border-radius: 12px;
          background: linear-gradient(135deg, rgba(139,47,201,0.1), rgba(240,77,160,0.1));
          border: 1px solid rgba(139,47,201,0.15); color: ${C_PURPLE};
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        }

        .pdf-btn {
          display: inline-flex; align-items: center; gap: 6px; padding: 6px 14px;
          background: rgba(240,77,160,0.08); color: ${C_PINK};
          border: 1px solid rgba(240,77,160,0.2); border-radius: 50px;
          font-size: 12.5px; font-weight: 700; text-decoration: none;
          transition: all 0.2s; white-space: nowrap;
        }
        .pdf-btn:hover {
          background: ${C_PINK}; color: #fff; transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(240,77,160,0.25);
        }
        
        .no-pdf-badge {
          display: inline-flex; padding: 6px 12px; background: #f8f9fa;
          color: #94a3b8; border: 1px solid #e2e8f0; border-radius: 50px;
          font-size: 11.5px; font-weight: 600;
        }

        .empty-state { padding: 40px 0; color: ${C_TEXT_MUTED}; }
        .empty-state svg { width: 32px; height: 32px; color: rgba(139,47,201,0.2); margin-bottom: 12px; }
        .empty-state p { margin: 0; font-weight: 500; }

        .glass { 
          background: #fff; border-radius: 20px; padding: 12px 24px 24px; 
          border: 1px solid rgba(139,47,201,0.08); box-shadow: 0 10px 40px rgba(0,0,0,0.04); 
          overflow: hidden;
        }

        .premium-table .ant-table { background: transparent !important; font-family: inherit; }
        .premium-table .ant-table-thead > tr > th {
          background: transparent !important; color: ${C_TEXT_MUTED};
          font-weight: 700; font-size: 12.5px; text-transform: uppercase; 
          letter-spacing: 0.05em; border-bottom: 2px solid rgba(139,47,201,0.08); padding: 20px 16px;
        }
        .premium-table .ant-table-thead > tr > th::before { display: none !important; }
        .premium-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid rgba(139,47,201,0.05); padding: 20px 16px; transition: background 0.3s; vertical-align: middle;
        }
        .premium-table .ant-table-tbody > tr:hover > td { background: #fcfaff !important; }
        .premium-table .ant-pagination { margin-top: 24px !important; }
        .premium-table .ant-pagination-item-active { border-color: ${C_PINK}; background: rgba(240,77,160,0.05); }
        .premium-table .ant-pagination-item-active a { color: ${C_PINK} !important; }

        /* <-- NEW: Modal Specific Styles --> */
        .abstract-modal .ant-modal-content {
          border-radius: 20px; overflow: hidden; padding: 24px 32px;
        }
        .modal-inner { margin-top: 20px; }
        .modal-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .modal-field { display: flex; flex-direction: column; gap: 6px; }
        .modal-lbl { 
          font-size: 11px; font-weight: 800; text-transform: uppercase; 
          letter-spacing: 0.08em; color: ${C_PURPLE}; 
        }
        .modal-val { font-size: 14.5px; font-weight: 600; color: ${C_TEXT_MAIN}; }
        .modal-badge {
          display: inline-block; width: fit-content; padding: 4px 12px; 
          background: ${C_SOFT_BG}; color: ${C_PINK}; border-radius: 50px; 
          font-size: 12.5px; font-weight: 700; border: 1px solid rgba(240,77,160,0.2);
          text-transform: capitalize;
        }
        .modal-divider {
          height: 1px; background: rgba(139,47,201,0.1); margin: 24px 0;
        }
        .modal-abstract-box {
          background: #f9f9fc; border: 1px solid #eef0f4; border-radius: 12px;
          padding: 16px 20px; font-size: 14px; line-height: 1.6; color: #475569;
          max-height: 200px; overflow-y: auto; font-weight: 500;
        }
        .modal-pdf-btn {
          display: flex; align-items: center; justify-content: center; gap: 10px;
          width: 100%; padding: 14px; background: ${C_GRAD}; color: #fff;
          border-radius: 12px; font-size: 15px; font-weight: 700; text-decoration: none;
          transition: all 0.2s; box-shadow: 0 8px 20px rgba(139,47,201,0.25);
        }
        .modal-pdf-btn:hover {
          transform: translateY(-2px); box-shadow: 0 12px 24px rgba(139,47,201,0.35); color: #fff;
        }

        @media(max-width: 768px) {
          .glass { padding: 12px; border-radius: 16px; }
          .header-flex { flex-direction: column; align-items: flex-start; gap: 16px; }
          .modal-grid { grid-template-columns: 1fr; gap: 16px; }
          .abstract-modal .ant-modal-content { padding: 20px; }
        }
      `}</style>
    </App>
  );
};

export default AbstractList;